import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Typography, useRouteDocumentTitle, Pagination, exportToExcel } from '@client/toolkit';
import { FaCalendarAlt } from 'react-icons/fa';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { Table } from './components';
import { OutstandingValueCard } from '../components';
import { dateRangeOptions } from '../utils/date-range-options';
import { useGetVacancyDetailsQuery, useGetVacancySummaryQuery, useLazyGetVacancyExportDetailsQuery } from '@client/api';
import { transformToExcelFormat } from './utils/transformToExcelFormat';
import { DateRangeFilter, notSameYear } from '../components/date-range-filter';
import dayjs from 'dayjs';

export function VacancyCostRoute() {
  const { t } = useTranslation('reporting');
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [triggerExport] = useLazyGetVacancyExportDetailsQuery();
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: dateRangeOptions().currentVsPreviousYear.startDate,
    endDate: dateRangeOptions().currentVsPreviousYear.endDate,
  });
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.Invoices);

  const {
    data,
    isLoading: summaryIsLoading,
    isFetching: summaryIsFetching,
  } = useGetVacancySummaryQuery({
    startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
    endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
  });

  const {
    data: vacancyDetails,
    isLoading: vacancyDetailsIsLoading,
    isFetching: vacancyDetailsIsFetching,
  } = useGetVacancyDetailsQuery({
    startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
    endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
    page: page,
    pageSize: 10,
  });

  const selectedYear = dayjs(timeRange.startDate).year();

  const onExportExcel = useCallback(async () => {
    setExporting(true);
    const result = await triggerExport({
      startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
      pageSize: 0,
    });
    if (result.isSuccess) {
      exportToExcel(transformToExcelFormat(result.data, selectedRows, selectedYear), 'vacancy-cost-report.xlsx');
    }
    setExporting(false);
  }, [timeRange, triggerExport, selectedRows, selectedYear]);

  return (
    <ReportPageContent
      breadcrumb={t('navigation.reporting.vacancyCost')}
      menu={
        <DateRangeFilter
          options={[dateRangeOptions().currentVsPreviousYear, dateRangeOptions().custom]}
          shouldDisableDate={notSameYear}
          onChange={(value) => setTimeRange({ startDate: value[0], endDate: value[1] })}
          onExportExcel={onExportExcel}
          exporting={exporting}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <OutstandingValueCard
          isLoading={summaryIsLoading || summaryIsFetching}
          title={t('unoccupiedCost', { year: dayjs(timeRange.startDate).year() })}
          amount={data?.selectedYearAmount}
        />
        <OutstandingValueCard
          isLoading={summaryIsLoading || summaryIsFetching}
          title={t('unoccupiedCost', { year: dayjs(timeRange.endDate).year() - 1 })}
          amount={data?.previousYearAmount}
        />
        <OutstandingValueCard
          isLoading={summaryIsLoading || summaryIsFetching}
          title={t('development')}
          amount={data?.variance.absolute}
          changeValue={data?.variance.percentage}
          prefix={data && data.variance.absolute.value > 0 ? '+' : ''}
          showChange
          isInverted
        />
      </div>
      <div>
        <span className="flex items-baseline gap-2 mb-4 text-sm">
          <FaCalendarAlt className="text-white" />
          <Typography className="text-white text-sm">
            {t('dateRange', {
              startDate: timeRange.startDate.toLocaleDateString('no-NO'),
              endDate: timeRange.endDate.toLocaleDateString('no-NO'),
            })}
          </Typography>
        </span>
        <Card className="pb-4">
          <Table
            data={vacancyDetails?.items ?? []}
            selectedYear={selectedYear}
            isLoading={vacancyDetailsIsFetching || vacancyDetailsIsLoading}
            onSelected={(ids) => {
              setSelectedRows(ids);
            }}
          />
          <Pagination
            totalCount={vacancyDetails?.total ?? 0}
            currentPage={page}
            setPage={setPage}
            pageSize={vacancyDetails?.pageSize ?? 10}
            visible={true}
          />
        </Card>
      </div>
    </ReportPageContent>
  );
}
